import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { getFirstArrayValue, isEmpty, sentenceCase } from '../../utils';
import moment from 'moment';

type Control = FormControl<string | null> | FormControl<number | null> | FormControl<boolean | null>;

export type InputType =
  | 'text'
  | 'email'
  | 'date'
  | 'time'
  | 'number'
  | 'checkbox'
  | 'numberStepper'
  | 'yesNo'
  | 'phoneNumber'
  | 'postCode'
  | 'pesel'
  | 'radio'
  | 'select'
  | 'groupedSelect'
  | 'textarea'
  | 'year';

export type AutocompleteType =
  | 'on'
  | 'off'
  | 'name'
  | 'given-name'
  | 'family-name'
  | 'postal-code'
  | 'address-line1'
  | 'address-line2'
  | 'address-line3'
  | 'address-level1'
  | 'address-level2'
  | 'address-level3'
  | 'address-level4';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface GroupedSelectOption {
  label: string;
  options: SelectOption[];
}

const YES_NO_TRUE = 0;
const YES_NO_FALSE = 1;

const SATURDAY = 6;
const SUNDAY = 7;
const DEFAULT_TEXTAREA_ROW_AMOUNT = 1;

@Component({
  selector: 'app-dynamic-input',
  templateUrl: './dynamic-input.component.html',
  styleUrls: ['./dynamic-input.component.scss'],
})
export class DynamicInputComponent implements AfterViewInit {
  @ViewChild('textAreaElement') textAreaElement?: ElementRef<HTMLTextAreaElement>;
  @Input() formGroup!: FormGroup;
  @Input() name!: string | number;
  @Input() label!: string;
  @Input() disabled = false;
  @Input() placeholder?: string;
  @Input() prefix?: string;
  @Input() suffix?: string;
  @Input() inputType: InputType = 'text';
  @Input() autocompleteType?: AutocompleteType;
  @Input() mask?: string;
  @Input() options: SelectOption[] = [];
  @Input() groupedOptions: GroupedSelectOption[] = [];
  @Input() optionFilter = true;
  @Input() labelSize: 'small' | 'big' = 'small';
  @Input() yesNoValues: { value: boolean; label: string }[] = [
    { value: true, label: 'common.YES' },
    { value: false, label: 'common.NO' },
  ];
  @Input() minLength: string | number | null = null;
  @Input() maxLength: string | number | null = null;
  @Input() min: string | number | null = null;
  @Input() max: string | number | null = null;
  @Input() maxRows: number | null = null; // for textarea
  @Input() minDate!: string | undefined;
  @Input() maxDate!: string | undefined;
  @Input() startViewDate: 'month' | 'year' | 'multi-year' = 'month';
  @Input() startAtDate!: Date | null;
  @Input() noWeekends?: boolean;
  @Input() error = false;
  @Input() errors?: ValidationErrors | null = null;

  showYearPicker = false;

  get control(): Control {
    return this.formGroup.get(this.name.toString()) as Control;
  }

  get hasError(): boolean {
    const controlErrors = this.control.invalid && this.control.touched;
    const customErrors = !isEmpty(Object.keys(this.errors || {}));
    const forceError = this.error;

    return forceError || customErrors || controlErrors;
  }

  get errorMessage(): Record<string, any> | null {
    if (!this.hasError) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const controlErrors = Object.entries(this.control.errors || {}).map(([key, params]) => ({ key, params }));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const customErrors = Object.entries(this.errors || {}).map(([key, params]) => ({ key, params }));

    const errors = [...controlErrors, ...customErrors].filter(Boolean);

    return getFirstArrayValue(errors);
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  get selectedRadioButton(): number | null {
    if (this.control.value === null) {
      return null;
    }

    return this.control.value ? YES_NO_TRUE : YES_NO_FALSE;
  }

  get formControlName(): string {
    return this.name.toString();
  }

  ngAfterViewInit(): void {
    if (this.textAreaElement) {
      this.textAreaElement.nativeElement.addEventListener('input', this.adjustHeight.bind(this));
    }
  }

  handleRadioButtonsChange(event: number): void {
    (this.control as FormControl<boolean | null>).setValue(this.yesNoValues[event].value);
  }

  handleYearChange(event: number): void {
    (this.control as FormControl<number | null>).setValue(event);
  }

  adjustHeight(): void {
    const textareaElem = this.textAreaElement?.nativeElement;
    if (!textareaElem) {
      return;
    }

    textareaElem.style.height = 'auto'; // Reset height to auto before calculating the scrollHeight

    // Reset rows to 1 to get the base scrollHeight
    textareaElem.rows = 1;
    let rowsRequired = Math.ceil(textareaElem.scrollHeight / parseFloat(getComputedStyle(textareaElem).fontSize));

    // Ensure we don't exceed maxRows if specified
    if (this.maxRows) {
      rowsRequired = Math.min(rowsRequired, this.maxRows);
    }

    textareaElem.rows = rowsRequired;

    if (!textareaElem.textLength) {
      textareaElem.rows = DEFAULT_TEXTAREA_ROW_AMOUNT;
    }
  }

  capitalizeFirstLetter(value: string): void {
    (this.control as FormControl<string | null>).setValue(sentenceCase(value));
  }

  handleChangeTextInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.capitalizeFirstLetter(value);
  }

  limitInput(event: KeyboardEvent) {
    if (!this.max) {
      return;
    }

    const input = event.target as HTMLInputElement;

    // Timeout to ensure we get the value after the key has been processed
    setTimeout(() => {
      if (parseInt(input.value, 10) > Number(this.max)) {
        (this.control as FormControl<string | null>).setValue(String(this.max));
        input.value = String(this.max);
      }
    });
  }

  handleKeyupTextInput(): void {
    const value = this.control.value;
    if (typeof value === 'string') {
      this.capitalizeFirstLetter(value);
    }
  }

  handlePasteTextInput(event: ClipboardEvent): void {
    event.preventDefault();
    const value = event.clipboardData?.getData('text');
    if (value) {
      this.capitalizeFirstLetter(value);
    }
  }

  filterOutWeekends(date: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const momentDate = moment(date);

    return momentDate.isoWeekday() !== SATURDAY && momentDate.isoWeekday() !== SUNDAY;
  }
}
